import { summarizeTrials } from '../core';
import type { ModuleSummary, TrialResult } from '../core/types';
import type { AnyRuntimeTrainingModule } from './runtime';

/** Derives the persisted summary from raw trials; also used to verify JSON imports. */
export function summarizeModuleResults(
  module: AnyRuntimeTrainingModule,
  trials: readonly TrialResult[],
): ModuleSummary {
  const moduleTrials = trials.filter((trial) => trial.moduleId === module.id);
  const threshold = module.getThreshold?.(moduleTrials);
  const summary = summarizeTrials(module.id, moduleTrials, {
    threshold: threshold?.value,
    thresholdUnit: threshold?.unit,
  });

  if (module.id === 'moving-target') return { ...summary, accuracyCi95: undefined };
  if (module.id === 'central-peripheral') return summary;
  if (module.id !== 'player-track') return summary;

  const valid = moduleTrials.filter((trial) => trial.valid);
  const matched = valid.reduce((total, trial) => {
    const value = trial.metadata.correctSelections;
    const targetCount = typeof trial.metadata.targetCount === 'number' ? trial.metadata.targetCount : 4;
    return total + (typeof value === 'number' ? value : trial.correct ? targetCount : 0);
  }, 0);
  const selections = valid.reduce((total, trial) =>
    total + (typeof trial.metadata.targetCount === 'number' ? trial.metadata.targetCount : 4), 0);
  const falseSelections = valid.reduce((total, trial) => {
    if (!trial.response) return total;
    const selected = trial.response.split(',').filter(Boolean).length;
    const correctSelections = typeof trial.metadata.correctSelections === 'number'
      ? trial.metadata.correctSelections
      : trial.correct ? selected : 0;
    return total + Math.max(0, selected - correctSelections);
  }, 0);
  return {
    ...summary,
    accuracy: selections ? matched / selections : 0,
    accuracyCi95: undefined,
    falseResponses: falseSelections,
  };
}
